import React, { useState } from 'react';
import moment from 'moment';
import { AiSchedule, AiScheduleRun } from '../../types/IAiSchedule';
import {
  useAiSchedule,
  useDeleteAiSchedule,
  usePauseAiSchedule,
  useResumeAiSchedule,
  useRunAiScheduleNow,
  useUpdateAiSchedule,
} from '../../hooks/reactQuery/useAiSchedules';

const formatLocal = (utc: string | null) =>
  utc ? moment.utc(utc).local().format('ddd, MMM D · h:mm a') : '—';

const describeRun = (run: AiScheduleRun): string => {
  if (run.status === 'Failed') return run.error ?? 'Unknown error';
  return run.sentTo ? `Emailed to ${run.sentTo}` : '';
};

const STATUS_LABEL: Record<AiSchedule['status'], string> = {
  Active: 'Active',
  Paused: 'Paused',
  Error: 'Stopped: errors',
};

interface IProps {
  schedule: AiSchedule;
}

// One schedule: what it does, when it runs, how the last runs went, and the actions on it.
const ScheduleCard = ({ schedule }: IProps) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(schedule.prompt);
  const [showRuns, setShowRuns] = useState(false);
  const update = useUpdateAiSchedule();
  const pause = usePauseAiSchedule();
  const resume = useResumeAiSchedule();
  const remove = useDeleteAiSchedule();
  const runNow = useRunAiScheduleNow();
  const detail = useAiSchedule(showRuns ? schedule.id : null);
  const busy =
    update.isLoading ||
    pause.isLoading ||
    resume.isLoading ||
    remove.isLoading ||
    runNow.isLoading;

  const save = () => {
    const prompt = draft.trim();
    if (prompt.length < 10 || prompt === schedule.prompt) {
      setEditing(false);
      setDraft(schedule.prompt);
      return;
    }
    update.mutate(
      { id: schedule.id, update: { prompt } },
      { onSuccess: () => setEditing(false) },
    );
  };

  const onDelete = () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete the schedule "${schedule.name}"?`)) return;
    remove.mutate({ id: schedule.id });
  };

  const runs = detail.data?.recentRuns ?? [];

  return (
    <div
      className={`schedule-card schedule-card--${schedule.status.toLowerCase()}`}
    >
      <div className="schedule-card-head">
        <b className="schedule-name">{schedule.name}</b>
        <span
          className={`schedule-status schedule-status--${schedule.status.toLowerCase()}`}
        >
          {STATUS_LABEL[schedule.status]}
        </span>
        <div className="schedule-actions">
          <button
            type="button"
            className="btn-app btn-app--outline btn-app--sm"
            onClick={() => {
              setShowRuns(true);
              runNow.mutate({ id: schedule.id });
            }}
            disabled={busy}
            title="Run the prompt now and email you the result"
          >
            {runNow.isLoading ? 'Running…' : 'Run now'}
          </button>
          {schedule.status === 'Active' ? (
            <button
              type="button"
              className="btn-app btn-app--outline btn-app--sm"
              onClick={() => pause.mutate({ id: schedule.id })}
              disabled={busy}
            >
              Pause
            </button>
          ) : (
            <button
              type="button"
              className="btn-app btn-app--primary btn-app--sm"
              onClick={() => resume.mutate({ id: schedule.id })}
              disabled={busy}
            >
              Resume
            </button>
          )}
          <button
            type="button"
            className="btn-app btn-app--danger btn-app--sm"
            onClick={onDelete}
            disabled={busy}
          >
            Delete
          </button>
        </div>
      </div>

      {runNow.data ? (
        <div
          className={`schedule-run-outcome schedule-run-outcome--${runNow.data.status.toLowerCase()}`}
          role="status"
        >
          {runNow.data.status === 'Succeeded'
            ? `Run finished and emailed to ${runNow.data.sentTo ?? 'you'}.`
            : `Run failed: ${runNow.data.error ?? 'unknown error'}`}
        </div>
      ) : null}
      {runNow.error ? (
        <div
          className="schedule-run-outcome schedule-run-outcome--failed"
          role="alert"
        >
          {runNow.error.message}
        </div>
      ) : null}

      {schedule.status === 'Error' ? (
        <div className="chat-error" role="alert">
          <b>
            Stopped after {schedule.consecutiveFailures} failed runs in a row.
          </b>
          <div className="chat-error-guidance">
            {schedule.lastError ?? 'See the run history below.'} Fix the prompt
            if needed, then Resume.
          </div>
        </div>
      ) : null}

      {editing ? (
        <div className="schedule-edit">
          <textarea
            aria-label="Prompt"
            rows={4}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={update.isLoading}
          />
          <div className="schedule-compose-actions">
            <button
              type="button"
              className="btn-app btn-app--primary btn-app--sm"
              onClick={save}
              disabled={update.isLoading || draft.trim().length < 10}
            >
              Save
            </button>
            <button
              type="button"
              className="btn-app btn-app--outline btn-app--sm"
              onClick={() => {
                setEditing(false);
                setDraft(schedule.prompt);
              }}
              disabled={update.isLoading}
            >
              Cancel
            </button>
            {update.error ? (
              <span className="schedule-error-text">
                {update.error.message}
              </span>
            ) : null}
          </div>
        </div>
      ) : (
        <blockquote className="schedule-prompt">
          {schedule.prompt}
          <button
            type="button"
            className="schedule-edit-btn"
            onClick={() => setEditing(true)}
            disabled={busy}
          >
            Edit prompt
          </button>
        </blockquote>
      )}

      <dl className="schedule-facts">
        <div>
          <dt>Runs</dt>
          <dd>{schedule.runs}</dd>
        </div>
        <div>
          <dt>Next run</dt>
          <dd>
            {schedule.status === 'Active'
              ? formatLocal(schedule.nextRunUtc)
              : '—'}
          </dd>
        </div>
        <div>
          <dt>Last run</dt>
          <dd>{formatLocal(schedule.lastRunUtc)}</dd>
        </div>
        <div>
          <dt>Task</dt>
          <dd>
            Email me
            {schedule.emailSubject ? `: “${schedule.emailSubject}”` : ''}
          </dd>
        </div>
      </dl>

      <button
        type="button"
        className="schedule-runs-toggle"
        onClick={() => setShowRuns((s) => !s)}
        aria-expanded={showRuns}
      >
        {showRuns ? 'Hide run history' : 'Run history'}
      </button>
      {showRuns ? (
        <div className="schedule-runs">
          {detail.isLoading ? (
            <div className="schedules-empty">Loading…</div>
          ) : null}
          {detail.data && runs.length === 0 ? (
            <div className="schedules-empty">
              This schedule has not run yet.
            </div>
          ) : null}
          {runs.length ? (
            <table>
              <thead>
                <tr>
                  <th>Started</th>
                  <th>Outcome</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((run) => (
                  <tr
                    key={run.id}
                    className={`schedule-run schedule-run--${run.status.toLowerCase()}`}
                  >
                    <td>{formatLocal(run.startedUtc)}</td>
                    <td>{run.status}</td>
                    <td>{describeRun(run)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default ScheduleCard;
